import { useEffect } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const LANG_MAP: Record<string, { hreflang: string; htmlLang: string; ogLocale: string }> = {
  zh: { hreflang: 'zh-CN', htmlLang: 'zh-CN', ogLocale: 'zh_CN' },
  en: { hreflang: 'en',    htmlLang: 'en',    ogLocale: 'en_US' },
};

const ALTERNATE_LANGS = Object.keys(LANG_MAP);

/**
 * Dynamically updates:
 *  - <html lang="...">
 *  - <link rel="alternate" hreflang="..."> for all supported languages
 *  - <link rel="canonical"> for the current language
 *  - <meta property="og:locale"> for current language
 */
export function useHreflang(lang: 'zh' | 'en') {
  useEffect(() => {
    const current = LANG_MAP[lang];
    if (!current) return;

    // 1. Update <html lang>
    document.documentElement.lang = current.htmlLang;

    // 2. Update canonical
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) {
      canonical.href = `${SITE_URL}/?lang=${lang}`;
    }

    // 3. Remove old dynamic hreflang links (keep the static ones from index.html)
    document
      .querySelectorAll<HTMLLinkElement>('link[rel="alternate"][data-dynamic-hreflang]')
      .forEach((el) => el.remove());

    // 4. Insert fresh hreflang alternates for all languages
    ALTERNATE_LANGS.forEach((l) => {
      const info = LANG_MAP[l];
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.setAttribute('hreflang', info.hreflang);
      link.href = `${SITE_URL}/?lang=${l}`;
      link.setAttribute('data-dynamic-hreflang', 'true');
      document.head.appendChild(link);
    });

    // 5. Add x-default (always points to root)
    const xDefault = document.createElement('link');
    xDefault.rel = 'alternate';
    xDefault.setAttribute('hreflang', 'x-default');
    xDefault.href = `${SITE_URL}/`;
    xDefault.setAttribute('data-dynamic-hreflang', 'true');
    document.head.appendChild(xDefault);

    // 6. Update og:locale
    const ogLocale = document.querySelector<HTMLMetaElement>('meta[property="og:locale"]');
    if (ogLocale) {
      ogLocale.content = current.ogLocale;
    }

    // 7. Update og:locale:alternate to the other language
    const ogAlternate = document.querySelector<HTMLMetaElement>(
      'meta[property="og:locale:alternate"]'
    );
    if (ogAlternate) {
      const alternateLang = ALTERNATE_LANGS.find((l) => l !== lang);
      if (alternateLang) {
        ogAlternate.content = LANG_MAP[alternateLang].ogLocale;
      }
    }

    // Cleanup: remove dynamic tags on unmount
    return () => {
      document
        .querySelectorAll<HTMLLinkElement>('link[rel="alternate"][data-dynamic-hreflang]')
        .forEach((el) => el.remove());
    };
  }, [lang]);
}
